import dayjs from "dayjs";

export type CalendarAvailabilityMap = Record<string, number>;

export type PlannerAvailabilityCalendarProps = {
  currentMonth: Date;
  availability: CalendarAvailabilityMap;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onDateClick: (dateStr: string, count: number) => void;
  selectedDate: string | null;
};

type SlotState = 'isBeforeToday' | 'available' | 'almostFull' | 'unavailable';

function getSlotState(count: number): SlotState {
  if (count === 0) return "unavailable";
  if (count <= 2) return "almostFull";
  return "available";
}

function getDayCellStyle(state: SlotState): {
  wrapper: string;
  text: string;
  count: string;
} {
  if (state === 'isBeforeToday') {
    return {
      wrapper: "bg-[#dedede] cursor-not-allowed",
      text: "text-[#9CA3AF] font-normal",
      count: "text-[#9CA3AF]",
    };
  }
  if (state === "unavailable") {
    return {
      wrapper: "bg-[#F3F4F6] cursor-not-allowed",
      text: "text-[#9CA3AF] font-normal",
      count: "text-[#9CA3AF]",
    };
  }
  if (state === "almostFull") {
    return {
      wrapper: "bg-[#FFFBEB] cursor-pointer hover:brightness-95 transition-all",
      text: "text-[#92400E] font-semibold",
      count: "text-[#92400E]/70",
    };
  }
  return {
    wrapper: "bg-[#d0e4ff] cursor-pointer hover:brightness-95 transition-all",
    text: "text-[#1E3A8A] font-semibold",
    count: "text-[#334155]/60",
  };
}

const MONTH_NAMES = [
  "1월", "2월", "3월", "4월", "5월", "6월",
  "7월", "8월", "9월", "10월", "11월", "12월",
];
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

export default function PlannerAvailabilityCalendar({
  currentMonth,
  availability,
  onPrevMonth,
  onNextMonth,
  onDateClick,
  selectedDate
}: PlannerAvailabilityCalendarProps) {
  const today = new Date();
  const todayStr = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startingDayOfWeek = new Date(year, month, 1).getDay();
  const monthPrefix = `${year}-${String(month + 1).padStart(2, "0")}`;

  return (
    <div className="px-4 pt-5 pb-4">
      <div className="bg-white rounded-2xl overflow-hidden border border-[#E5E7EB]/60">

        {/* Month Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#E5E7EB]/60" >
          <button
            onClick={onPrevMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            aria-label="이전 달"
          >
            <i className="ri-arrow-left-s-line text-xl text-[#374151]"></i>
          </button>

          <span className="text-sm sm:text-base md:text-lg font-semibold text-[#111827]">
            {year}년 {MONTH_NAMES[month]}
          </span>

          <button
            onClick={onNextMonth}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            aria-label="다음 달"
          >
            <i className="ri-arrow-right-s-line text-xl text-[#374151]"></i>
          </button>
        </div>

        {/* Day Labels */}
        <div className="grid grid-cols-7 border-b border-[#E5E7EB]/60">
          {DAY_NAMES.map((day, idx) => (
            <div
              key={day}
              className={`text-center text-xs sm:text-sm md:text-base font-medium py-3 ${
                idx === 0
                  ? "text-rose-400"
                  : idx === 6
                  ? "text-[#60A5FA]"
                  : "text-[#9CA3AF]"
              }`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Date Grid */}
        <div className="grid grid-cols-7 gap-1.5 p-3">
          {Array.from({ length: startingDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}

          {Array.from({ length: daysInMonth }).map((_, index) => {
            const day = index + 1;
            const dateStr = `${monthPrefix}-${String(day).padStart(2, "0")}`;
            const isBeforeToday = dayjs(dateStr).isBefore(dayjs(todayStr), "day");
            const count = availability[dateStr] ?? 0;
            const state = isBeforeToday ? "isBeforeToday" : getSlotState(count);
            const styles = getDayCellStyle(state);
            const isToday = dateStr === todayStr;
            const dayOfWeek = (startingDayOfWeek + index) % 7;

            const sundayOverride =
              dayOfWeek === 0 && state !== 'isBeforeToday' &&  state !== "unavailable" ? "!text-rose-500" : "";
            const saturdayOverride =
              dayOfWeek === 6 && state !== 'isBeforeToday' && state !== "unavailable" ? "!text-[#3B82F6]" : "";
            const isNotAvailable = state === "unavailable" || isBeforeToday;

            const isSelectedDate = selectedDate === dateStr;
            const styleResult = isSelectedDate ? styles.wrapper.replace('bg-[#d0e4ff]', '') : styles.wrapper
            return (
              <button
                key={day}
                onClick={() => state !== "unavailable" && onDateClick(dateStr, count)}
                disabled={isNotAvailable}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center relative select-none ${styleResult} ${isToday ? "border-2 border-blue-700" : ""} ${isSelectedDate ? "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer" : ""}`}
              >
                {/* {isToday && (
                  <span className="absolute top-1 right-1.5 w-1 h-1 rounded-full bg-[#2563EB]" />
                )} */}
                <span
                  className={`text-sm sm:text-base md:text-xl leading-none ${styles.text} ${sundayOverride} ${saturdayOverride} ${isSelectedDate ? " text-white" : ""}`}
                >
                  {day}
                </span>
                {isBeforeToday ? (
                  <span
                    className={`text-[10px] sm:text-xs md:text-sm leading-none mt-1 ${styles.count} ${isSelectedDate ? " text-white" : ""}`}
                  >-</span>

                ) : (
                  <span
                    className={`text-[10px] sm:text-xs md:text-sm leading-none mt-1 ${styles.count} ${isSelectedDate ? " text-white" : ""}`}
                  >
                    {count}건
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-3 bg-white text-sm rounded-xl border border-[#E5E7EB]/60 px-4 py-3.5 text-[#464646]">
          날짜를 선택하시면 예약 가능한 일정이 나옵니다.
        </div>
      </div>
      
      {/* Legend */}
      <div className="mt-3 bg-white rounded-xl border border-[#E5E7EB]/60 px-4 py-3.5" style={{display: 'none'}}>
        <p className="text-[11px] sm:text-xs font-semibold text-[#9CA3AF] uppercase tracking-widest mb-3">
          범례
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#EFF6FF] border border-[#BFDBFE]/60 flex items-center justify-center">
              <span className="text-[9px] sm:text-[11px] font-bold text-[#1E3A8A]">14</span>
            </div>
            <span className="text-xs sm:text-sm text-[#6B7280]">3개 이상 가능</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#FFFBEB] border border-[#FDE68A]/60 flex items-center justify-center">
              <span className="text-[9px] sm:text-[11px] font-bold text-[#92400E]">14</span>
            </div>
            <span className="text-xs sm:text-sm text-[#6B7280]">1~2개 가능</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#F3F4F6] border border-[#E5E7EB]/60 flex items-center justify-center">
              <span className="text-[9px] sm:text-[11px] font-bold text-[#9CA3AF]">14</span>
            </div>
            <span className="text-xs sm:text-sm text-[#6B7280]">예약 불가</span>
          </div>
        </div>
      </div>
    </div>
  );
}
